<template>
  <div v-if="!isFilteredOut && (beingSearched || hasSearched)" 
  class="row justify-left no-wrap full-width" @click.stop.prevent="elementClicked">
    <div v-if="(isSelectable || (isExpandable && !showRecords))" :class="['row no-wrap', { 'rec-lst-item full-width': !(isExpandable && showRecords) }]">
      <q-checkbox
        class="rec-checkbox unset-items-center"
        :checked-icon="hideCheckboxes ? 'done' : undefined"
        :unchecked-icon="hideCheckboxes ? 'none' : undefined"
        size="xs"
        @click.stop.prevent
        v-model="selectedModel"
      />
      <q-item-label v-if="!(isExpandable && showRecords)" color="accent" style="margin-top: 5px;">
        <span v-for="(span, i) in getSearchedName" :key="i" :style="span.style">{{ span.text }}</span>
      </q-item-label>
    </div>
    <q-expansion-item
      v-if="isExpandable && showRecords"
      switch-toggle-side
      expand-icon-toggle
      :model-value="hasSearched || (beingSearched && searchRegex !== undefined) || openOnStart"
      :dense="true"
      :dense-toggle="true"
      expand-separator
      icon="expand_more"
      class="recursive-element full-width"
    >
      <template #header>
        <!-- <div style="margin-top: 3px;">{{ getElementName }}</div> -->
      <q-item-label style="margin-top: 6px;" class="rec-lst-item">
        <span v-for="(span, i) in getSearchedName" :key="i" :style="span.style">{{ span.text }}</span>
      </q-item-label>
      </template>
        <div v-if="returnType === 'none'" class="recursive-element--child">
          <recursive-selector-element
            v-for="(child, index) in theModel[nestedKeyName]"
            :element="child"
            :key="parentIdx+'_'+index"
            :element-chain="[]"
            :parent-idx="index"
            :is-root="false"
            :take-ids-only="takeIdsOnly"
            :return-type="returnType"
            :nested-key-name="nestedKeyName"
            :entity-inside-name="entityInsideName"
            :element-type="elementType"
            :allow-multiple="allowMultiple"
            :hide-checkboxes="hideCheckboxes"
            :show-records="showRecords"
            :filter-out-list="filterOutList"
            :parameter-type-name="parameterTypeName"
            :value-type="child.ValueType"
            :search-regex="searchRegex"
            :filter-string="filterString"
            :parent-included="beingSearched"
            v-model="theModel[nestedKeyName][index]"
          />
        </div>
        <div v-else class="recursive-element--child">
          <recursive-selector-element
            v-for="(child, index) in (element as any)[nestedKeyName]"
            :element="child"
            :key="parentIdx+'_'+index"
            :element-chain="returnType === 'chain' ? 
              (takeIdsOnly ? (elementType === 1 ? [...elementChain, child.Id] : (
                elementType === 9 ? [...elementChain, child[entityInsideName].Id] : [...elementChain, child])
              ) : [...elementChain, child]) : []"
            :parent-idx="index"
            :is-root="false"
            :take-ids-only="takeIdsOnly"
            :return-type="returnType"
            :nested-key-name="nestedKeyName"
            :entity-inside-name="entityInsideName"
            :element-type="elementType"
            :allow-multiple="allowMultiple"
            :hide-checkboxes="hideCheckboxes"
            :show-records="showRecords"
            :filter-out-list="filterOutList"
            :parameter-type-name="parameterTypeName"
            :value-type="child.ValueType"
            :search-regex="searchRegex"
            :filter-string="filterString"
            :parent-included="beingSearched"
            v-model="theModel"
          />
        </div>
    </q-expansion-item>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { CommonEntity } from '@/models/interfaces/common';
import { VariableType } from '@/models/etc/types';
import { arrayEquals, findRecursive, foreachRecursive } from '@/models/helpers/recursive';
import { reactive } from 'vue';
import { tokenizeSearch, buildHighlightSpans } from '@/components/common/basic/search-highlight/searchHighlight';

// for matching string values that are in a wrong keyboard layout
const ruLayout = require('convert-layout/ru');

@Component({
  name: 'RecursiveSelectorElement',
  expose: ['toggle']
})
export default class RecursiveSelectorElement extends Vue {
  @Prop({ default: undefined })
  element!: any
  // Здесь важно понимать, что в элементе может быть любой класс.
  // Конкретная интерпретация зависит от того, какой отображающий компонент 
  // из data-components стоит в RecursiveSelectorElement.

  // В модель вернётся массив выбранных элементов 
  // (или цепочек элементов вверх по рекурсии до родительских элементов)
  @Prop({ default: [] })
  modelValue!: any
  // update v-model
  @Emit("update:modelValue") emitModelValue(arg: any[]) { return arg; }
  @Emit("elementToggled") emitToggled(arg: boolean) { return arg; }

  @Prop({ default: false })
  allowMultiple?: boolean
  // works if elementType === VariableType.Entity
  @Prop({ default: false })
  takeIdsOnly?: boolean
  @Prop({ default: false })
  hideCheckboxes?: boolean
  @Prop({ default: true })
  showRecords?: boolean
  @Prop({ default: 'valueType' })
  parameterTypeName!: string
  // if false then the model is an array of selected elements (e.g. [selected_el1, selected_el2, ...])
  // if true then the model is an array of selected element chains 
  // (e.g. [[selected_el1_root, ..., selected_el1_parent, selected_el1], [selected_el2_root, ..., selected_el2_parent, selected_el2], ...])
  @Prop({ default: 'single-element' })
  returnType?: string
  @Prop({ default: VariableType.Entity })
  elementType!: VariableType
  @Prop({ default: '' })
  nestedKeyName!: string
  @Prop({ default: '' })
  entityInsideName!: string
  @Prop({ default: false })
  isColumn!: string

  @Prop({ default: false })
  isRoot!: boolean
  @Prop({ default: undefined })
  parentIdx!: string | undefined
  @Prop({ default: [] })
  elementChain!: any[]

  @Prop({ default: 0 })
  valueType!: number
  @Prop({ default: [] })
  filterOutList?: any[]
  @Prop({ default: undefined })
  searchRegex?: RegExp
  // parent included in search
  @Prop({ default: false })
  parentIncluded!: boolean
  @Prop({ default: false })
  openOnStart!: boolean
  @Prop({ default: '' })
  filterString?: string

  // for search
  allMatches: Array<any> = [];
  allMatchesHas: Array<any> = [];

  // for name highlight
  allMatchesName: Array<any> = [];
  allMatchesHasName: Array<any> = [];

  get theModel(): any {
    return this.modelValue
  }

  set theModel(val: any) {
    this.emitModelValue(val)
  }

  modelElement(el: any) {
    return this.isColumn ? (el.IdChain) : (this.elementType === 1 && this.takeIdsOnly ? el.Id : el)
  }

  toggle() {
    this.selectedModel = !this.selectedModel;
  }

  elementClicked() {
    if (this.isSelectable || (this.isExpandable && !this.showRecords) && !this.hideCheckboxes) {
      this.toggle()
      this.emitToggled(this.selectedModel)
    }
  }

  get hasSelectedChildren() {
    let result = false
    if (this.isSelectable && this.returnType === 'none' && this.theModel && this.theModel[this.nestedKeyName]) {
      // Check if at least some descendant Selectable elements of the element are selected
      // Only for expandable parameters
      
      foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return
        (element.Selectable && element.Selected) && (result = true)

        if (element.FieldFilters) {
          foreachRecursive(a, el_to_find, element.FieldFilters)
        }
        return
      }, undefined, this.theModel[this.nestedKeyName])
    }
    return result
  }

  get indexInModel() {
    if (!this.theModel || !Array.isArray(this.theModel)) { return -1 }
    const find = this.theModel.findIndex((el: any) => {
      if (!this.isColumn) {
        if (this.returnType === 'chain') {
          return arrayEquals(el, this.elementChain)
        } else {
          return el == this.element
        }
      } else {
        return arrayEquals(el.IdChain, this.element.IdChain)
      }
    })
    return find
  }

  get selectedModel() {
    if (!this.isColumn && this.returnType === 'none' && this.isSelectable) {
      return this.theModel.Selected
    }
    return this.indexInModel !== -1
  }

  // if returnType === 'none' then just change .Selected field in the element
  // Otherwise: 
  // if returnType === 'chain' then arg should contain element itself, its check status and chain_to_parent
  // chain_to_parent should include element also ([element_root_parent, ..., element_parent, element])
  // otherwise arg should contain element itself, its check status only
  set selectedModel(arg: boolean) {
    if (this.returnType === 'none') {
      if (!this.theModel.Selected || !this.hasSelectedChildren) {
        this.theModel.Selected = arg
        this.theModel.Positive != undefined && (this.theModel.Positive = arg)
      }
    } else {
      // if new element checked
      if (arg) {
        if (!this.selectedModel) {
          if (this.allowMultiple) {
            // add new element to the model if not added yet
            this.theModel = [...this.theModel, (this.returnType === 'chain' ? this.elementChain : this.modelElement(this.element))]
          } else {
            // replace whole model with newly selected element
            this.theModel = [this.returnType === 'chain' ? this.elementChain : this.modelElement(this.element)]
          }
        }
      // if new element unchecked
      } else {
        if (this.selectedModel) {
          // if (this.allowMultiple) {
            // delete element from the model if found
            this.theModel.splice(this.indexInModel, 1)
            // trigger redraw
            this.theModel = [...this.theModel]
          // }
        }
      }
    }
  }

  get isSelectable(): boolean {
    return this.returnType === 'none' ? 
      this.theModel.Selectable : 
      !(this.element && this.element[this.nestedKeyName] && this.element[this.nestedKeyName].length)
  }

  get isFilteredOut(): boolean {
    return this.filterOutList && this.parameterTypeName ? this.filterOutList.includes(this.parameterTypeName) : false
  }
  
  get hasSearched(): boolean {
    this.allMatchesHas = reactive([])
    this.allMatchesName = reactive([])
    let tempMatches: any[] = []
    let tempMatchesName: any[] = []

    const search_in = this.isExpandable ? this.theModel : this.element;
    if (search_in && search_in[this.nestedKeyName]) {
      const that = this
      
      const param = findRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element || !el_to_find) return undefined

        if (that.entityInsideName && element[that.entityInsideName]) {
          const matches = []
          const matchesN = []
          let res
          const translatedName = ruLayout.toEn(element[that.entityInsideName].Name)
          while ((res = el_to_find.exec(element[that.entityInsideName].Name))) {
            if (res) {
              for (const r of res) {
                matches.push(r);
                (r != element[that.entityInsideName].Name) && matchesN.push(r);
              }
            }
          }
          while ((res = el_to_find.exec(translatedName))) {
            if (res) {
              for (const r of res) {
                matches.push(ruLayout.fromEn(r));
                (r != translatedName) && matchesN.push(ruLayout.fromEn(r));
              }
            }
          }
          // set to remove duplicates
          tempMatches = Array.from(new Set([ ...tempMatches, ...matches ]))
          tempMatchesName = Array.from(new Set([ ...tempMatchesName, ...matchesN ]))

          if (matches.length) {
            return element;
          }

        } else {
          const matches = []
          const matchesN = []
          let res
          const translatedName = ruLayout.toEn(element.Name)
          while ((res = el_to_find.exec(element.Name))) {
            if (res) {
              for (const r of res) {
                matches.push(r);
                (r != element.Name) && matchesN.push(r);
              }
            }
          }
          while ((res = el_to_find.exec(translatedName))) {
            if (res) {
              for (const r of res) {
                matches.push(ruLayout.fromEn(r));
                (r != translatedName) && matchesN.push(ruLayout.fromEn(r));
              }
            }
          }
          // set to remove duplicates
          tempMatches = Array.from(new Set([ ...tempMatches, ...matches ]))
          tempMatchesName = Array.from(new Set([ ...tempMatchesName, ...matchesN ]))

          if (matches.length) {
            return element;
          }
        }

        if (element && element[that.nestedKeyName]) {
          return findRecursive(a, el_to_find, element[that.nestedKeyName])
        }
        return undefined
      }, that.searchRegex, search_in[that.nestedKeyName])

      this.allMatchesHas = reactive(tempMatches)
      this.allMatchesHasName = reactive(tempMatchesName)

      // true if Name suits or contains child
      return param ? true : false
    }
    return false
  }

  get beingSearched(): boolean {
    this.allMatches = reactive([]);
    this.allMatchesName = reactive([])

    if (!this.searchRegex) {
      return true
    }

    const matches = []
    const matchesN = []
    let res
    const translatedName = ruLayout.toEn(this.getElementName)
    while ((res = this.searchRegex.exec(this.getElementName))) {
      if (res) {
        for (const r of res) {
          matches.push(r);
          (r != this.getElementName) && matchesN.push(r);
        }
      }
    }
    while ((res = this.searchRegex.exec(translatedName))) {
      if (res) {
        for (const r of res) {
          matches.push(ruLayout.fromEn(r));
          (r != translatedName) && matchesN.push(ruLayout.fromEn(r));
        }
      }
    }

    // set to remove duplicates
    this.allMatches = Array.from(new Set([ ...this.allMatches, ...matches ]))
    this.allMatchesName = Array.from(new Set([ ...this.allMatchesName, ...matchesN ]))

    const searched_result = matches.length ? true : false;
    return searched_result || this.parentIncluded
  }

  get isExpandable(): boolean {
    return this.returnType === 'none' ? 
      (this.theModel && this.theModel[this.nestedKeyName] ? this.theModel[this.nestedKeyName].length > 0 : false) : 
      (this.element && this.element[this.nestedKeyName] ? (this.element[this.nestedKeyName].length || this.element.ValueType === 5) : false)
  }

  get getSearchedName(): Array<{ text: string; style: string }> {
    if (!this.filterString?.trim()) return [{ text: this.getElementName, style: '' }];
    const tokens = tokenizeSearch(this.filterString);
    if (!tokens.length) return [{ text: this.getElementName, style: '' }];
    return buildHighlightSpans(this.getElementName, tokens);
  }

  get getElementName(): string {
    const el = this.returnType === 'none' ? this.theModel : this.element

    switch(this.elementType) {
      case VariableType.Entity:
        return (el as any)[this.isColumn ? 'compound_name' : 'Name']
      case VariableType.EntityInside:
        return (el[this.entityInsideName] as CommonEntity).Name
      case VariableType.Date:
        return el.formatDate(new Date(""+el), 'DD.MM.YYYY')
      case VariableType.DateTime:
        return el.formatDate(new Date(""+el), 'DD.MM.YYYY HH:mm:ss')
      case VariableType.Boolean:
        return el ? this.$t('yes_label') : this.$t('no_label')
      case VariableType.Number:
      case VariableType.String:
      default:
        return ""+el
    }
  }
}
</script>

<style lang="scss">
.recursive-element {
  &--child {
    margin-left: 26px !important;
  }
}

.recursive-element {
  .q-item__section--avatar {
    padding-left: 0 !important;
  }
}

.rec-checkbox {
  font-size: 13px !important;
}
</style>